import React, { useEffect, useRef, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CircularProgress,
    FormControl,
    TextField,
    Checkbox,
    AutocompleteRenderGetTagProps,
    Chip,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { FmtpError, FmtpErrorCode } from '@lanterds/types';
import './OSDManifestForm.scss';
import Header from 'src/components/common/Header/Header';
import { getOsdManifestRoutes } from 'src/api/reports';
import { SnackbarKey, useSnackbar } from 'notistack';
import useDebounce from 'src/hooks/useDebounce';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

type OsdFormProps = {
    handleGenerateReport: () => void;
    isGeneratingReport: boolean;
    selectedRoute: string[] | null;
    setSelectedRoute: (route: string[] | null) => void;
};

interface FieldErrors {
    routeError?: string;
}

const initialDebounceValue = '';

export default function OSDForm({ handleGenerateReport, isGeneratingReport, selectedRoute, setSelectedRoute }: OsdFormProps) {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [routes, setRoutes] = useState<string[] | null>(null);
    const [isFetchingRoutes, setIsFetchingRoutes] = useState<boolean>(false);
    const [routeNameOpen, setRouteNameOpen] = useState(false);
    const [inputValue, setInputValue] = useState(initialDebounceValue);
    const { debouncedValue, isDebouncing } = useDebounce(inputValue, initialDebounceValue, 1000);
    const snackbarRef = useRef<SnackbarKey | null>(null);
    const abortControllerRef = useRef<AbortController>(new AbortController());

    useEffect(() => {
        if (!routeNameOpen && isFetchingRoutes) {
            abortControllerRef.current.abort();
        }
    }, [routeNameOpen, isFetchingRoutes, abortControllerRef]);

    useEffect(() => {
        return () => {
            if (snackbarRef.current) {
                closeSnackbar(snackbarRef.current);
                snackbarRef.current = null;
            }
        };
    }, [closeSnackbar]);

    useEffect(() => {
        if (selectedRoute && fieldErrors) {
            setFieldErrors({});
        }
    }, [selectedRoute]);

    useEffect(() => {
        if (debouncedValue && routeNameOpen) {
            getRoutes(debouncedValue);
        } else {
            setRoutes(null);
            closeSnackbar();
            snackbarRef.current = null;
        }
    }, [debouncedValue, routeNameOpen]);

    const getRoutes = async (routeName: string) => {
        // Help prevent race conditions by aborting previous requests
        abortControllerRef.current.abort();

        // Create a new controller instance for each fetch
        const thisAbortController = new AbortController();
        abortControllerRef.current = thisAbortController;

        snackbarRef.current = enqueueSnackbar('Searching routes...', {
            variant: 'loading',
            preventDuplicate: true,
            disableWindowBlurListener: true,
            autoHideDuration: null,
            action: (key) => (
                <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                    Dismiss
                </Button>
            ),
        });

        setIsFetchingRoutes(true);

        getOsdManifestRoutes(routeName, thisAbortController.signal)
            .then((data) => {
                setRoutes(data);
            })
            .catch((err) => {
                const message = 'An error occurred while getting routes';
                const errorCode: FmtpErrorCode = 'SHU017';
                console.error(err);
                throw new FmtpError({
                    message,
                    errorCode,
                });
            })
            .finally(() => {
                if (snackbarRef.current) {
                    closeSnackbar(snackbarRef.current);
                    snackbarRef.current = null;
                }
                setIsFetchingRoutes(false);
            });
    };

    const validateForm = () => {
        let isValid = true;
        const newFieldErrors: FieldErrors = {};

        if (!selectedRoute) {
            newFieldErrors.routeError = 'Please select a Route.';
            isValid = false;
        }

        if (isValid) {
            setFieldErrors({});
        } else {
            setFieldErrors(newFieldErrors);
        }

        return isValid;
    };

    const handleGenerate = () => {
        const isValid = validateForm();
        if (isValid) {
            handleGenerateReport();
        }
    };

    const getNoOptionsText = () => {
        if (debouncedValue.trim().length === 0) {
            return 'Begin typing to search';
        } else if (isFetchingRoutes || isDebouncing || !routes) {
            // Prevents showing a premature message before "Searching routes..."
            // appears during debounce.
            return 'Searching routes…';
        } else {
            return 'No matching routes found';
        }
    };

    const renderTags = (value: string[], getTagProps: AutocompleteRenderGetTagProps) => {
        const numTags = value.length;
        const limitTags = 1;
        return (
            <>
                {value.slice(0, limitTags).map((option: string, index: number) => (
                    <Chip {...getTagProps({ index })} key={index} label={option} />
                ))}

                {numTags > limitTags && ` +${numTags - limitTags}`}
            </>
        );
    };

    return (
        <Box id="osd-container">
            <Card id="osd-form">
                <Grid xs={12} container spacing={2}>
                    <Grid xs={12}>
                        <Header text="OS&D Manifest" />
                    </Grid>
                    <Grid xs={12}>
                        <FormControl fullWidth>
                            <Autocomplete
                                id="routes-input"
                                multiple={true}
                                disableCloseOnSelect={true}
                                open={routeNameOpen}
                                onOpen={() => {
                                    setRouteNameOpen(true);
                                }}
                                onClose={() => {
                                    setRouteNameOpen(false);
                                    setRoutes(null);
                                    setInputValue(initialDebounceValue);
                                }}
                                options={routes ?? []}
                                selectOnFocus
                                clearOnBlur
                                handleHomeEndKeys
                                loading={isFetchingRoutes}
                                isOptionEqualToValue={(option, value) => option === value}
                                renderTags={renderTags}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        label="Route"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {isFetchingRoutes ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                        InputLabelProps={{ ...params.InputLabelProps }}
                                        error={!!fieldErrors?.routeError}
                                        helperText={fieldErrors?.routeError}
                                    />
                                )}
                                renderOption={(props, option, { selected }) => {
                                    return (
                                        <li {...props}>
                                            <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
                                            {option}
                                        </li>
                                    );
                                }}
                                // when in multi select mode, does not accept null for empty values
                                value={selectedRoute ?? undefined}
                                onInputChange={(event, value) => {
                                    // Event is null on init, so skip in that case
                                    if (event && event.type === 'change' && value) {
                                        setInputValue(value);
                                    }
                                }}
                                onChange={(_event, newValue) => {
                                    setSelectedRoute(newValue);
                                }}
                                disabled={isFetchingRoutes && !routeNameOpen}
                                noOptionsText={getNoOptionsText()}
                                loadingText="Searching routes…"
                            />
                        </FormControl>
                    </Grid>
                    <Grid container xs={12} justifyContent="center">
                        <Grid>
                            <Button
                                variant="contained"
                                size="medium"
                                onClick={handleGenerate}
                                disabled={isGeneratingReport || isFetchingRoutes}
                            >
                                {isGeneratingReport ? 'Generating…' : 'Generate Report'}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Card>
        </Box>
    );
}
